import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './PromoteSection.css';
import {
  listPublishedPublications,
  listPublishedPublicationsByCompany,
} from '../../../services/publicationsApi';
import {
  listPromotions,
  promoteResource,
  removePromotion,
} from '../../../services/promotionsApi';
import usePromotionLookup from '../../../hooks/usePromotionLookup';

const keyFor = (type, id) => `${type}:${id}`;

const stripHtmlTags = value => {
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
};

const RESOURCE_PROMOTION_PACKAGES = [
  {
    id: 'boost-6h',
    label: 'Impulso 6 horas',
    durationLabel: '6 horas continuas',
    reach: 'Perfecto para demandas inmediatas o lanzamientos relámpago.',
    creditCost: 40,
  },
  {
    id: 'boost-24h',
    label: 'Visibilidad diaria',
    durationLabel: '24 horas',
    reach: 'Mantente en la parte superior durante todo el día.',
    creditCost: 95,
  },
  {
    id: 'boost-3d',
    label: 'Semana táctica',
    durationLabel: '3 días',
    reach: 'Ideal para campañas de seguimiento y generación de leads.',
    creditCost: 220,
  },
];

const COMPANY_PROMOTION_PACKAGES = [
  {
    id: '6h',
    label: 'Impulso 6 horas',
    durationLabel: '6 horas',
    reach: 'Ideal para picos de demanda y eventos puntuales.',
    creditCost: 40,
    approximateValue: 480,
    icon: 'bolt',
    bullets: ['Alta prioridad en búsquedas', 'Ideal para ventanas flash.'],
  },
  {
    id: '24h',
    label: 'Visibilidad diaria',
    durationLabel: '24 horas',
    reach: 'Mantente arriba en resultados durante todo el día.',
    creditCost: 95,
    approximateValue: 1140,
    icon: 'star',
    bullets: ['Presencia destacada todo el día', 'Monitoreo constante de clics.'],
    isFeatured: true,
  },
  {
    id: '7d',
    label: 'Semana destacada',
    durationLabel: '7 días',
    reach: 'Cobertura constante para captar clientes recurrentes.',
    creditCost: 540,
    approximateValue: 6480,
    icon: 'crown',
    bullets: ['Cobertura semanal estable', 'Ideal para captar clientes frecuentes.'],
  },
];

const mapPublicationCard = item => ({
  id: item.id,
  title: item.title || item.unitName || 'Publicacion sin titulo',
  snippet: stripHtmlTags(item.excerpt || item.description || ''),
  location: item.location || '',
  image:
    item.primaryImageUrl ||
    item.productImageUrl ||
    item.unitPhotoUrl ||
    item.bannerUrl ||
    item.companyHeroUrl ||
    item.companyLogoUrl ||
    '',
  created: item.created,
  updated: item.updated,
});

const RechargeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M15.833 10.417a5.833 5.833 0 0 1-9.9 4.125l-1.35 1.35a.417.417 0 0 1-.708-.295v-3.75c0-.23.187-.417.417-.417h3.75a.417.417 0 0 1 .295.708l-1.31 1.31a4.167 4.167 0 1 0 4.928-6.552.417.417 0 0 1 .135-.812h2.5c.23 0 .417.187.417.417zM4.167 9.583a5.833 5.833 0 0 1 9.9-4.125l1.35-1.35a.417.417 0 0 1 .708.295v3.75a.417.417 0 0 1-.417.417h-3.75a.417.417 0 0 1-.295-.708l1.31-1.31a4.167 4.167 0 1 0-4.928 6.552.417.417 0 0 1-.135.812h-2.5a.417.417 0 0 1-.417-.417z"
      fill="currentColor"
    />
  </svg>
);

const PlanIcon = ({ variant = 'bolt' }) => {
  if (variant === 'star') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="m12 3.5 2.62 5.3 5.85.85-4.23 4.13 1 5.82L12 17.9l-5.24 2.76 1-5.82-4.23-4.13 5.85-.85z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    );
  }
  if (variant === 'crown') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M3.5 9.5 7 13l2.6-6 2.4 6 2.6-6L17 13l3.5-3.5v8.5H3.5z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M10 2 6 13h4v9l4-11h-4z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="m4 8 2.5 2.5L12 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const resolveTarget = search => {
  const params = new URLSearchParams(search || '');
  return params.get('target') === 'company' ? 'company' : 'resources';
};

export default function PromoteSection({
  empresaId,
  companyCredits = 0,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTarget, setActiveTarget] = useState(() => resolveTarget(location.search));
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(() => activeTarget !== 'company');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState({});
  const [promotionMap, setPromotionMap] = useState({});
  const { refresh: refreshPromotions } = usePromotionLookup();
  const [selectedPackage, setSelectedPackage] = useState('');
  const [packageFeedback, setPackageFeedback] = useState('');
  const [resourceSelections, setResourceSelections] = useState({});
  const [resourceFeedbackMap, setResourceFeedbackMap] = useState({});
  const [infoDismissed, setInfoDismissed] = useState(false);
  const [openSelectKey, setOpenSelectKey] = useState('');
  const selectRefs = useRef({});

  useEffect(() => {
    setActiveTarget(resolveTarget(location.search));
  }, [location.search]);

  useEffect(() => {
    setSelectedPackage('');
    setPackageFeedback('');
    setResourceSelections({});
    setResourceFeedbackMap({});
    setLoading(activeTarget !== 'company');
  }, [activeTarget]);

  const fetchPromotions = useCallback(async () => {
    try {
      const records = await listPromotions();
      const map = {};
      records.forEach(item => {
        map[keyFor(item.resourceType, item.resourceId)] = item.id;
      });
      setPromotionMap(map);
    } catch (err) {
      console.warn('No fue posible obtener las promociones existentes.', err);
      setPromotionMap({});
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  useEffect(() => {
    if (activeTarget === 'company') {
      setLoading(false);
      return undefined;
    }

    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const pubs = await (empresaId
          ? listPublishedPublicationsByCompany(empresaId, { limit: 100 })
          : listPublishedPublications({ limit: 100 }));

        if (!ignore) {
          setPublications(Array.isArray(pubs) ? pubs.map(mapPublicationCard) : []);
        }
      } catch (err) {
        console.error('No fue posible cargar las publicaciones promocionables.', err);
        if (!ignore) {
          setError(
            err?.message ||
              'No pudimos obtener las publicaciones disponibles para promocionar.'
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [empresaId, activeTarget]);

  const isPromoted = useCallback(
    (type, id) => Boolean(promotionMap[keyFor(type, id)]),
    [promotionMap]
  );

  const handlePromoteToggle = async (resourceType, resourceId) => {
    if (!resourceType || !resourceId) return;
    const key = keyFor(resourceType, resourceId);
    const isActive = Boolean(promotionMap[key]);

    if (!isActive && resourceType === 'publication') {
      const selectedPackageId = resourceSelections[key];
      const selectedPackageEntry =
        RESOURCE_PROMOTION_PACKAGES.find(pkg => pkg.id === selectedPackageId) || null;
      if (!selectedPackageEntry) {
        setResourceFeedbackMap(prev => ({
          ...prev,
          [key]: {
            type: 'error',
            message: 'Selecciona un paquete de visibilidad antes de promocionar este recurso.',
          },
        }));
        return;
      }
      if (selectedPackageEntry.creditCost > ppcCredits) {
        setResourceFeedbackMap(prev => ({
          ...prev,
          [key]: {
            type: 'error',
            message: `Necesitas ${formatNumber(selectedPackageEntry.creditCost - ppcCredits)} créditos adicionales para activar "${selectedPackageEntry.label}".`,
          },
        }));
        return;
      }
    }

    setProcessing(prev => ({ ...prev, [key]: true }));
    try {
      if (isActive) {
        await removePromotion(promotionMap[key]);
        setPromotionMap(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setResourceFeedbackMap(prev => ({
          ...prev,
          [key]: {
            type: 'info',
            message: 'Promoción desactivada. Puedes seleccionar otro paquete cuando desees reactivarla.',
          },
        }));
      } else {
        const record = await promoteResource(resourceType, resourceId);
        setPromotionMap(prev => ({
          ...prev,
          [key]: record.id,
        }));
        const selectedPackageEntry =
          RESOURCE_PROMOTION_PACKAGES.find(pkg => pkg.id === resourceSelections[key]) || null;
        if (selectedPackageEntry) {
          setResourceFeedbackMap(prev => ({
            ...prev,
            [key]: {
              type: 'success',
              message: `Promoción activa por ${selectedPackageEntry.durationLabel}. Se descontarán ${formatNumber(selectedPackageEntry.creditCost)} créditos de tu saldo.`,
            },
          }));
        }
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('flotte:promotions-updated'));
      }
      refreshPromotions();
    } catch (err) {
      console.error('No fue posible actualizar la promocion.', err);
      setError(err?.message || 'No pudimos actualizar la promocion. Intenta de nuevo.');
    } finally {
      setProcessing(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const publicationCards = useMemo(() => publications, [publications]);
  const ppcCredits = useMemo(() => {
    const numeric = Number(companyCredits);
    return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
  }, [companyCredits]);
  const formatNumber = useCallback(value => {
    const numeric = Number(value) || 0;
    return numeric.toLocaleString('es-MX');
  }, []);

  const formatCurrency = useCallback(value => {
    const numeric = Number(value) || 0;
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(numeric);
  }, []);

  const handleSelectPackage = useCallback(
    pkg => {
      if (!pkg) return;
      setSelectedPackage(pkg.id);
      if (pkg.creditCost > ppcCredits) {
        setPackageFeedback(
          `Necesitas ${formatNumber(pkg.creditCost - ppcCredits)} créditos adicionales para activar "${pkg.label}".`
        );
        return;
      }
      setPackageFeedback(
        `"${pkg.label}" utilizará ${formatNumber(pkg.creditCost)} créditos por ${pkg.durationLabel}. Confirmaremos la deducción antes de activarla.`
      );
    },
    [formatNumber, ppcCredits]
  );

  const handleSelectResourcePackage = useCallback(
    (resourceType, resourceId, packageId) => {
      const key = keyFor(resourceType, resourceId);
      if (!packageId) {
        setResourceSelections(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setResourceFeedbackMap(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }
      const selected = RESOURCE_PROMOTION_PACKAGES.find(pkg => pkg.id === packageId);
      if (!selected) return;
      setResourceSelections(prev => ({
        ...prev,
        [key]: packageId,
      }));
      setResourceFeedbackMap(prev => ({
        ...prev,
        [key]: {
          type: selected.creditCost > ppcCredits ? 'error' : 'info',
          message:
            selected.creditCost > ppcCredits
              ? `Necesitas ${formatNumber(selected.creditCost - ppcCredits)} créditos adicionales para activar "${selected.label}".`
              : `"${selected.label}" utilizará ${formatNumber(selected.creditCost)} créditos por ${selected.durationLabel}.`,
        },
      }));
    },
    [formatNumber, ppcCredits]
  );

  useEffect(() => {
    if (!openSelectKey) return undefined;
    const handleClickAway = event => {
      const current = selectRefs.current?.[openSelectKey];
      if (current && !current.contains(event.target)) {
        setOpenSelectKey('');
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setOpenSelectKey('');
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSelectKey]);

  const companyView = (
    <div className="campaign-collection">
      <header className="campaign-collection__head">
        <h1>Promocionar empresa</h1>
      </header>

      <div className="pricing-grid">
        {COMPANY_PROMOTION_PACKAGES.map(pkg => {
          const insufficient = pkg.creditCost > ppcCredits;
          const isSelected = selectedPackage === pkg.id;
          const showRechargeCta = ppcCredits <= 0;
          const buttonDisabled = showRechargeCta ? false : insufficient;
          return (
            <article
              key={pkg.id}
              className={`pricing-card${isSelected ? ' is-selected' : ''}${insufficient && !showRechargeCta ? ' is-disabled' : ''}`}
            >
              <header className="pricing-card__head">
                <div className="pricing-card__head-top">
                  <span className={`pricing-card__icon pricing-card__icon--${pkg.icon || 'bolt'}`}>
                    <PlanIcon variant={pkg.icon} />
                  </span>
                  <div>
                    <p className="pricing-card__title">{pkg.label}</p>
                    <p className="pricing-card__meta">{pkg.durationLabel}</p>
                  </div>
                </div>
                {pkg.isFeatured ? (
                  <span className="pricing-card__badge">Recomendado</span>
                ) : null}
              </header>
              <div className="pricing-card__price">
                <span className="pricing-card__credits">{formatNumber(pkg.creditCost)}</span>
                <span className="pricing-card__unit">créditos</span>
              </div>
              <ul className="pricing-card__list">
                {(pkg.bullets || []).map(point => (
                  <li key={`${pkg.id}-${point}`}>
                    <span className="pricing-card__check">
                      <CheckIcon />
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="pricing-card__value">{formatCurrency(pkg.approximateValue)} aprox.</p>
              <button
                type="button"
                className={`pricing-card__cta${showRechargeCta ? ' is-outline' : ''}`}
                onClick={() =>
                  showRechargeCta ? navigate('/checkout/credits') : handleSelectPackage(pkg)
                }
                disabled={buttonDisabled}
              >
                {showRechargeCta ? (
                  <span className="campaign-row__button-content">
                    <span className="campaign-row__button-icon">
                      <RechargeIcon />
                    </span>
                    Recargar
                  </span>
                ) : (
                  'Activar'
                )}
              </button>
            </article>
          );
        })}
      </div>

      {packageFeedback ? (
        <div className="pricing-feedback" role="status">
          {packageFeedback}
        </div>
      ) : null}
    </div>
  );

  const resourcesView = (
    <div className="campaign-collection">
      <header className="campaign-collection__head">
        <h1>Promocionar publicaciones</h1>
      </header>

      {error ? <div className="promote-alert promote-alert--error">{error}</div> : null}

      {loading ? (
        <div className="promote-blank-state">Cargando recursos disponibles...</div>
      ) : publicationCards.length ? (
        <div className="resource-grid">
          {publicationCards.map(item => {
            const promoted = isPromoted('publication', item.id);
            const key = keyFor('publication', item.id);
            const updatedLabel = item.updated
              ? new Date(item.updated).toLocaleDateString('es-MX')
              : null;
            const selectedPackageId = resourceSelections[key] || '';
            const selectedPackage = selectedPackageId
              ? RESOURCE_PROMOTION_PACKAGES.find(pkg => pkg.id === selectedPackageId) || null
              : null;
            const feedback = resourceFeedbackMap[key];
            const isProcessing = Boolean(processing[key]);
            const shouldShowRechargeCta = !promoted && ppcCredits <= 0;
            const actionDisabled =
              isProcessing || (!promoted && !selectedPackageId && !shouldShowRechargeCta);
            const assetInitial = item.title?.[0]?.toUpperCase() || 'P';
            const selectLabelId = `plan-${item.id}`;
            const isSelectOpen = openSelectKey === key;
            return (
              <article
                key={item.id}
                className={`resource-card${promoted ? ' is-live' : ''}`}
              >
                <header className="resource-card__header">
                  <div className="resource-card__media">
                    {item.image ? (
                      <img src={item.image} alt={item.title} loading="lazy" />
                    ) : (
                      <div className="resource-card__placeholder" aria-hidden="true">
                        {assetInitial}
                      </div>
                    )}
                  </div>
                  <div className="resource-card__identity" title={item.title}>
                    <p className="resource-card__title">{item.title}</p>
                    <span className={`resource-status${promoted ? ' is-live' : ''}`}>
                      <span className="resource-status__dot" aria-hidden="true" />
                      <span>{promoted ? 'Live' : 'Pausado'}</span>
                    </span>
                  </div>
                </header>
                {item.snippet ? (
                  <p className="resource-card__description">{item.snippet}</p>
                ) : null}
                <div className="resource-card__meta-line">
                  {item.location ? <span>{item.location}</span> : null}
                  {updatedLabel ? <span>Actualizado {updatedLabel}</span> : null}
                </div>
                <div className="resource-card__control">
                  <span className="sr-only" id={selectLabelId}>
                    Selecciona duración
                  </span>
                  <div
                    className={`campaign-select${isSelectOpen ? ' is-open' : ''}`}
                    role="group"
                    aria-labelledby={selectLabelId}
                    ref={node => {
                      if (node) {
                        selectRefs.current[key] = node;
                      } else {
                        delete selectRefs.current[key];
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="campaign-select__trigger"
                      aria-haspopup="listbox"
                      aria-expanded={isSelectOpen}
                      onClick={() =>
                        setOpenSelectKey(prev => (prev === key ? '' : key))
                      }
                    >
                      <span
                        className={`campaign-select__value${selectedPackage ? '' : ' is-placeholder'}`}
                      >
                        {selectedPackage ? `${selectedPackage.durationLabel}` : 'Selecciona duración'}
                      </span>
                      <svg
                        className="campaign-select__chevron"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M5.833 7.5 10 11.667 14.167 7.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <ul className="campaign-select__menu" role="listbox">
                      <li>
                        <button
                          type="button"
                          className={`campaign-select__option${selectedPackageId ? '' : ' is-active'}`}
                          onClick={() => {
                            handleSelectResourcePackage('publication', item.id, '');
                            setOpenSelectKey('');
                          }}
                        >
                          <span>Sin selección</span>
                        </button>
                      </li>
                      {RESOURCE_PROMOTION_PACKAGES.map(pkg => (
                        <li key={pkg.id}>
                          <button
                            type="button"
                            className={`campaign-select__option${selectedPackageId === pkg.id ? ' is-active' : ''}`}
                            onClick={() => {
                              handleSelectResourcePackage('publication', item.id, pkg.id);
                              setOpenSelectKey('');
                            }}
                          >
                            <span>{pkg.durationLabel}</span>
                            <span className="campaign-select__chip">
                              {formatNumber(pkg.creditCost)} créditos
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="resource-card__footer">
                  <div className="resource-card__cost">
                    <span>Total</span>
                    <strong>
                      {selectedPackage
                        ? `${formatNumber(selectedPackage.creditCost)} créditos`
                        : '—'}
                    </strong>
                    <small>
                      {selectedPackage ? selectedPackage.durationLabel : 'Selecciona un plan'}
                    </small>
                  </div>
                  <button
                    type="button"
                    className={`campaign-row__button${
                      promoted ? ' is-ghost' : shouldShowRechargeCta ? ' is-outline' : ''
                    }`}
                    onClick={() => {
                      if (shouldShowRechargeCta) {
                        navigate('/checkout/credits');
                        return;
                      }
                      handlePromoteToggle('publication', item.id);
                    }}
                    disabled={Boolean(actionDisabled)}
                  >
                    {isProcessing ? (
                      'Guardando...'
                    ) : promoted ? (
                      'Pausar'
                    ) : shouldShowRechargeCta ? (
                      <span className="campaign-row__button-content">
                        <span className="campaign-row__button-icon">
                          <RechargeIcon />
                        </span>
                        Recargar
                      </span>
                    ) : (
                      'Activar'
                    )}
                  </button>
                </div>
                {feedback ? (
                  <p
                    className={`plan-feedback plan-feedback--${feedback.type}`}
                    role={feedback.type === 'error' ? 'alert' : 'status'}
                  >
                    {feedback.message}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="promote-empty-card">
          <div className="promote-empty-card__art" aria-hidden="true">
            📣
          </div>
          <div>
            <h3>Sin publicaciones elegibles</h3>
            <p>
              Publica una oferta o servicio para poder promocionarlo. Una vez aprobada, aparecerá en
              este panel.
            </p>
            <button
              type="button"
              className="promote-empty-card__cta"
              onClick={() => navigate('/dashboard/publications')}
            >
              Crear publicación
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section className="promote-page">
      <div className="promote-page__inner">
        <div className="promote-header">
          <p className="promote-masthead__label">Ad Manager</p>
          <div className="promote-wallet-pill" aria-live="polite">
            <span className="promote-wallet-pill__icon" aria-hidden="true">💳</span>
            <div>
              <span className="promote-wallet-pill__label">Créditos</span>
              <strong>{formatNumber(ppcCredits)}</strong>
            </div>
            <button
              type="button"
              className="promote-wallet-pill__button"
              onClick={() => navigate('/checkout/credits')}
            >
              Recargar
            </button>
          </div>
        </div>

        {!infoDismissed ? (
          <div className="promote-info-strip" role="status">
            <span className="promote-info-strip__icon" aria-hidden="true">ℹ️</span>
            <p>Los créditos se descuentan al activar. Pausa o reanuda sin perder valor.</p>
            <button
              type="button"
              aria-label="Cerrar recordatorio"
              onClick={() => setInfoDismissed(true)}
            >
              &times;
            </button>
          </div>
        ) : null}

        {activeTarget === 'company' ? companyView : resourcesView}
      </div>
    </section>
  );
}
